/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { RatingsSummary } from '@msdyn365-commerce/commerce-entities';
import { createObservableDataAction, IAction, IActionContext, IActionInput, IAny, ICreateActionContext, IGeneric, sendRequest } from '@msdyn365-commerce/core';
import { GetRatingsSummaryInput, getSelectedProductIdFromActionInput } from './index';
import { getRnrEndpointId, getRnrPartnerId } from './utilities/getRnrKeys';

export const createInput = (input: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    const productId = getSelectedProductIdFromActionInput(input);
    if (productId) {
        return new GetRatingsSummaryInput(productId, getRnrPartnerId(input), getRnrEndpointId(input));
    }
    throw new Error('[GetRatingsSummary] Not all necessary parameters found while creating Input for getRatingsSummaryApi');
};

/**
 * This function gets the ratings summary
 */
async function getRatingsSummary(input: GetRatingsSummaryInput, ctx: IActionContext): Promise<RatingsSummary> {
    if (!inputIsValid(input)) {
        ctx.trace(`[GetRatingsSummary] Failed to get ratings summary for product`);
        return {};
    }

    const requestUrl = `${input.serviceEndpoint}/v2.0/ratings/product/${input.productId}/summary?` +
        `tenantId=${input.tenantId}`;
    return sendRequest<RatingsSummary>(requestUrl, 'get').then((response) => {
        return response.data;
    }).catch((error) => {
        ctx.trace(`[GetRatingsSummary] Failed to get ratings summary for product`);
        throw error;
    });
}

function inputIsValid(input: GetRatingsSummaryInput): boolean {
    return input && input.productId.length > 0 && input.tenantId.length > 0;
}

export const getRatingsSummaryDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-ratings-summary',
    action: <IAction<RatingsSummary>>getRatingsSummary,
    input: createInput
});

export default getRatingsSummaryDataAction;